// DetailPageHeader/DetailPageHeader.tsx
import { Breadcrumbs } from '@nautilusbio/nb-design-system/Breadcrumbs';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { Link } from '@tanstack/react-router';
import React, { memo, type ReactNode } from 'react';

import styles from './styles.module.css';

export interface DetailPageBreadcrumb {
  id: string;
  label: string;
  to?: string;
  params?: Record<string, string>;
}

export interface DetailPageAction {
  id: string;
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  tooltip?: string;
  testId: string;
}

export interface DetailPageHeaderProps {
  breadcrumbs: DetailPageBreadcrumb[];
  actions?: DetailPageAction[];
}

function ActionButton({ action }: { action: DetailPageAction }) {
  const button = (
    <Button
      className={styles.action}
      data-testid={action.testId}
      isDisabled={action.disabled}
      onPress={action.onClick}
      size="md"
      visual="unstyled"
    >
      {action.icon}
      <Text as="span" noWrap size="md">
        {action.label}
      </Text>
    </Button>
  );

  if (action.disabled && action.tooltip !== undefined) {
    return <Tooltip content={action.tooltip} placement="bottom" trigger={button} />;
  }
  return button;
}

function DetailPageHeaderComponent({ breadcrumbs, actions }: DetailPageHeaderProps) {
  return (
    <Stack alignItems="center" className={styles.header} justifyContent="space-between" orientation="horizontal">
      <Breadcrumbs
        links={breadcrumbs.map(b => ({ id: b.id, children: b.label, to: b.to, params: b.params }))}
        renderLink={(link, _index, defaultLinkProps) => {
          if (link === undefined) return null;
          if (link.to === undefined) return link.children;
          return (
            <Link className={defaultLinkProps.className} params={link.params} to={link.to}>
              {link.children}
            </Link>
          );
        }}
        separator="arrow"
        size="md"
      />

      {actions !== undefined && actions.length > 0 && (
        <Stack gap={4} orientation="horizontal">
          {actions.map(action => (
            <ActionButton action={action} key={action.id} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export const DetailPageHeader = memo(DetailPageHeaderComponent);
